import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  api,
  type RunListStatus,
  type RunRow,
  type RunSummary,
  type StepRunSummary,
  type WorkflowSummary,
} from '../api.ts';
import { PaneResizer, usePaneWidth } from '../components/PaneResizer.tsx';
import { RunTimeline } from '../components/RunTimeline.tsx';
import { StepChat } from '../components/StepChat.tsx';
import { WorkspaceChangesCard } from '../components/WorkspaceChangesCard.tsx';

/**
 * Runs and the per-step conversations behind them.
 *
 * Forking lives here rather than in the builder because a fork is a fact about
 * a *run*, not about a workflow: you keep everything the step had learned up to
 * that point and change what happens next.
 *
 * This is the studio's one screen with an address of its own, and everything
 * else in reef that links to a run links here — Slack when a run starts,
 * finishes or wants an approval, and a detection that fired inside one.
 *
 * **Which workflow and which run are the shell's state, not this screen's.**
 * They are in the URL, and the URL is one copy; a second copy here that syncs
 * both ways is not a convenience but a feedback loop, and it behaves exactly as
 * one — picking a workflow set the state, the state rewrote the path, the path
 * pushed back an older selection, and the two fought each other for as many
 * round trips as the browser would allow. Everything the address bar does not
 * carry — the run list, the steps, which chat is open — is still owned here.
 *
 * The other half of that rule: **what is merely a default is derived, never
 * written**. "The first workflow" and "its newest run" are what this screen
 * shows when the path names neither, and an effect that wrote them into the
 * path instead raced the reader — it was scheduled against the render before
 * their click, so it answered a question they had already moved past and put
 * the picker back where it started. Only a choice writes: a workflow picked, a
 * run clicked, a fork taken, a link followed.
 *
 * **Two screens behind one rail destination.** `/runs` — reached from the
 * rail, from Home, from the palette — is every recent run across the
 * deployment, because the question somebody brings there is "what ran" and
 * they should not have to know which workflow to ask. `/workflows/:id/runs/…`
 * is the detail: one workflow's runs down the side and the open run in the
 * middle, at the address Slack posts. They are two components rather than one
 * with a branch, because the branch would have to sit above the detail's
 * hooks — and React needs the same hooks in the same order on every render of
 * one component. The rail destination does not change between the list and
 * the detail, so the instance would be the same one, rendering no hooks on
 * the way in and a dozen on the way out.
 */
export function RunsView({
  workflows,
  workflowId,
  runId,
  stepId,
  onOpenRun,
}: {
  workflows: WorkflowSummary[];
  /** From the path. Null when the screen was reached from the rail. */
  workflowId: string | null;
  runId: string | null;
  /** From the path too: the step whose chat a link asked to have open. */
  stepId: string | null;
  onOpenRun(workflowId: string | null, runId: string | null): void;
}) {
  if (!workflowId) return <RunsIndex onOpenRun={onOpenRun} />;
  return (
    <RunDetail
      workflows={workflows}
      workflowId={workflowId}
      runId={runId}
      stepId={stepId}
      onOpenRun={onOpenRun}
    />
  );
}

function RunDetail({
  workflows,
  workflowId,
  runId,
  stepId,
  onOpenRun,
}: {
  workflows: WorkflowSummary[];
  workflowId: string | null;
  runId: string | null;
  stepId: string | null;
  onOpenRun(workflowId: string | null, runId: string | null): void;
}) {
  const [sidebarWidth, setSidebarWidth] = usePaneWidth('reef.runs.sidebar', 232);
  const [chatWidth, setChatWidth] = usePaneWidth('reef.runs.chat', 400);
  /*
   * Carried with the workflow they describe, so a list that has not caught up
   * with the picker is ignored rather than shown. Kept as a bare array, the
   * frame between choosing a workflow and its runs arriving still had the
   * previous workflow's runs in it — which is also the frame this screen would
   * have picked a default run out of.
   */
  const [listed, setListed] = useState<{ workflowId: string; runs: RunSummary[] } | null>(null);
  /*
   * The run as the control plane described it, which the list cannot always
   * answer: `listRuns` caps at 50, so a link to a run from three months ago
   * resolves fine and is simply not in the sidebar. Without this the screen
   * would call its status "unknown" and hide the Pause and Cancel buttons on a
   * run that is still going.
   */
  const [openRun, setOpenRun] = useState<{ id: string; workflowId: string; status: string } | null>(
    null,
  );
  const [steps, setSteps] = useState<StepRunSummary[]>([]);
  /*
   * Which chat is open, carried with the run it was opened from — the same rule
   * as the run list above, and for a sharper reason. The chat pane renders
   * outside the block that waits for the run to answer, so between clicking
   * another run and its detail arriving, the previous run's step was still on
   * screen with a Fork button beside it. Forking reads the *run* from the
   * selection and the *step* from that pane, so pressing it in that window
   * forked the newly opened run from a boundary belonging to the one before
   * it — a real run, started against a step the reader never chose for it.
   */
  const [stepSelection, setStepSelection] = useState<{
    runId: string;
    step: StepRunSummary;
  } | null>(null);
  const [forking, setForking] = useState(false);
  /*
   * Bumped by an action that changes what the list says — a pause, a cancel, a
   * fork. It re-runs the load below rather than writing `listed` a second time
   * from the handler, and that is the whole point: a handler's own refresh
   * carries the workflow it was started under, so one that lands after the
   * reader has moved on installs the *previous* workflow's runs, the derivation
   * below rejects them as another workflow's, and the sidebar is left blank
   * with nothing scheduled to fill it. Going through the effect means the
   * selection changing cancels the refresh, like every other read here.
   */
  const [runsEpoch, setRunsEpoch] = useState(0);
  const [effect, setEffect] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * The path when it names one, the first workflow otherwise — and the workflow
   * list arrives after the first render, which reaching this screen by its own
   * URL is what made visible. Derived rather than remembered, so there is
   * nothing to go stale when the shell's fetch lands.
   */
  const activeWorkflowId = workflowId ?? workflows[0]?.id ?? null;
  const runs = listed?.workflowId === activeWorkflowId ? listed.runs : null;
  /* Same rule for the run: the one that was linked or clicked, else the newest. */
  const activeRunId = runId ?? runs?.[0]?.id ?? null;
  /* And the chat pane closes the moment the run under it changes, rather than
   * when its replacement arrives. */
  const selectedStep = stepSelection?.runId === activeRunId ? stepSelection.step : null;

  /*
   * What is on screen *now*, for the one caller that finishes later than the
   * render it started in. `control` awaits two requests, and the reader can
   * open another run while it waits — the closure would then install an answer
   * about the run they left, describing it under the new run's heading and
   * leaving the new one stuck on "Opening run…" forever, because its own load
   * has already been and gone. The effects below need no ref: their cleanup
   * runs when the selection changes, which is what `current` reads.
   */
  const onScreenRunId = useRef(activeRunId);
  onScreenRunId.current = activeRunId;

  /*
   * Both loads below drop their result if the selection moved on while they were
   * in flight. Two requests started a moment apart do not have to land in that
   * order, and the failure is a quiet one: pick a workflow with no runs, then
   * pick one with forty, and the slower empty response paints "No runs yet" over
   * a list that had arrived. Nothing looks broken — the runs are simply not
   * there until something else triggers a reload.
   */
  useEffect(() => {
    if (!activeWorkflowId) return;
    let current = true;
    api
      .listRuns(activeWorkflowId)
      .then((result) => current && setListed({ workflowId: activeWorkflowId, runs: result.runs }))
      .catch((caught) => current && setError((caught as Error).message));
    return () => {
      current = false;
    };
  }, [activeWorkflowId, runsEpoch]);

  useEffect(() => {
    if (!activeRunId) {
      setSteps([]);
      setStepSelection(null);
      setOpenRun(null);
      return;
    }
    let current = true;
    api
      .getRun(activeRunId)
      .then((result) => {
        if (!current) return;
        setOpenRun(result.run);
        setSteps(result.steps);
        setStepSelection(result.steps[0] ? { runId: result.run.id, step: result.steps[0] } : null);
        setError(null);
        /*
         * The run says which workflow it belongs to, and it outranks the path: a
         * link built by hand, or one whose workflow segment has gone stale,
         * would otherwise leave the sidebar listing one workflow's runs beside
         * another workflow's run. Written, unlike the defaults above, because it
         * corrects a URL somebody is holding rather than filling one in.
         */
        if (result.run.workflowId !== activeWorkflowId) {
          onOpenRun(result.run.workflowId, result.run.id);
        }
      })
      .catch((caught) => {
        if (!current) return;
        setOpenRun(null);
        setSteps([]);
        setStepSelection(null);
        /*
         * Named, because this is what a stale or mistyped link looks like and a
         * bare "404 Not Found" over an empty screen sends the reader to ask in a
         * channel whether reef is broken. A run nobody may see 404s exactly like
         * a run that does not exist — deliberately, so the API does not confirm
         * that a private workflow has runs — so the sentence has to allow both.
         */
        setError(
          `Run ${activeRunId} did not open — it does not exist, or it belongs to a workflow you cannot see. (${(caught as Error).message})`,
        );
      });
    return () => {
      current = false;
    };
  }, [activeRunId, activeWorkflowId, onOpenRun]);

  /*
   * The step the path names, once the run's steps are here to find it in. The
   * work board's drawer links to one step's chat, and without this the link
   * landed on the run with its *first* step open — a working link to the wrong
   * conversation. Keyed on the steps rather than folded into the load above so
   * a step chosen by clicking stays chosen: the path's step is applied when the
   * steps arrive or the path changes, and a click in between is a click.
   * A step the run does not have falls through to the load's default.
   */
  useEffect(() => {
    if (!stepId || !activeRunId) return;
    const step = steps.find((entry) => entry.stepId === stepId);
    if (step) setStepSelection({ runId: activeRunId, step });
  }, [stepId, steps, activeRunId]);

  const runStatus =
    runs?.find((run) => run.id === activeRunId)?.status ??
    (openRun?.id === activeRunId ? openRun.status : 'unknown');

  const control = async (intent: 'cancel' | 'pause' | 'resume') => {
    if (!activeRunId || !activeWorkflowId) return;
    setError(null);
    try {
      const result = await api.controlRun(activeRunId, intent);
      setEffect(result.effect);
      /*
       * The run itself as well as the list, because the list is not always the
       * one holding the answer: it caps at 50, so a run linked from Slack that
       * is no longer in it kept the status it was opened with — and a pause
       * that had taken hold still offered Pause.
       *
       * Re-read rather than infer what the intent did. Resuming a run that was
       * awaiting approval when somebody paused it goes back to awaiting
       * approval rather than to running, and cancelling moves nothing yet by
       * design — a status assumed from the verb would state both wrongly.
       */
      setRunsEpoch((epoch) => epoch + 1);
      const detail = await api.getRun(activeRunId);
      if (onScreenRunId.current === detail.run.id) setOpenRun(detail.run);
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  const fork = async (step: StepRunSummary, message: string) => {
    if (!activeRunId || !activeWorkflowId) return;
    setForking(true);
    try {
      const result = await api.forkRun(activeRunId, {
        stepId: step.stepId,
        message,
        scope: 'subtree',
      });
      setRunsEpoch((epoch) => epoch + 1);
      /*
       * Only if the reader is still where they started it. Forking is a
       * deliberate destination — you asked for a new run, you expect to land on
       * it — but a fork takes a moment, and one that finishes after they have
       * opened something else drags them off that instead. A destination they
       * chose two clicks ago is no longer one they are asking for.
       */
      if (onScreenRunId.current === activeRunId) onOpenRun(activeWorkflowId, result.runId);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setForking(false);
    }
  };

  return (
    <div
      className={`body resizable ${selectedStep ? 'with-inspector' : ''}`}
      style={
        {
          '--sidebar-w': `${sidebarWidth}px`,
          '--inspector-w': `${chatWidth}px`,
        } as CSSProperties
      }
    >
      <aside className="sidebar">
        {/*
          Back to the list, spelled as a path change: `/runs` with neither id.
          A button that hid the detail in local state would leave the address
          bar pointing at a run that is no longer on screen — the one thing
          this screen exists not to do.
        */}
        <button className="linkish back-link" onClick={() => onOpenRun(null, null)}>
          ← All runs
        </button>
        <p className="section-title">Workflow</p>
        {/*
          A workflow and no run, which is what lets the newest one be derived
          once this workflow's list arrives. Naming a run here would have to be
          the *previous* workflow's, which is the run this screen would then
          spend a frame opening under the new workflow's heading.
        */}
        <select
          value={activeWorkflowId ?? ''}
          onChange={(event) => onOpenRun(event.target.value, null)}
        >
          {workflows.map((workflow) => (
            <option key={workflow.id} value={workflow.id}>
              {workflow.name}
            </option>
          ))}
        </select>

        <p className="section-title" style={{ marginTop: '1rem' }}>
          Runs
        </p>
        {runs?.length === 0 && <p className="hint">No runs yet.</p>}
        {/*
          A run's headline is what started it, not what became of it. Leading
          every row with a filled status chip made a list of forty runs read as
          forty alerts; the dot carries the same fact in the margin, and the
          status stays as a word on the second line where it belongs.
        */}
        {runs?.map((run) => (
          <button
            key={run.id}
            className="list-item run-row"
            aria-current={run.id === activeRunId}
            onClick={() => onOpenRun(activeWorkflowId, run.id)}
          >
            <span className={`run-dot ${statusTone(run.status)}`} aria-hidden="true" />
            <span className="run-row-body">
              <span className="run-row-head">
                {run.triggerType}
                {run.forkedFromRun && <span className="run-tag">fork</span>}
              </span>
              <small>
                {run.status} · {relativeTime(run.createdAt)}
              </small>
            </span>
          </button>
        ))}
      </aside>

      <PaneResizer
        label="Run list width"
        edge="left"
        width={sidebarWidth}
        min={160}
        max={560}
        reset={232}
        onChange={setSidebarWidth}
      />

      <main className="main">
        {error && (
          <div className="banner danger">
            <h3>Something went wrong</h3>
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        {!activeRunId && <div className="empty">Pick a run to see its steps.</div>}

        {/*
          Held until the run itself has answered, rather than rendering the
          header off the id in the URL. A link to a run that does not exist
          otherwise drew "Run run_… / this run has not started any steps yet",
          which is a confident description of a run Reef could not find — the
          reader is told the run is fine and empty when it is neither.
        */}
        {activeRunId && !openRun && !error && <div className="empty">Opening run…</div>}

        {activeRunId && openRun?.id === activeRunId && (
          <>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ margin: '0 0 0.25rem' }}>Run <code>{activeRunId}</code></h1>
                <p className="hint" style={{ marginTop: 0 }}>
                  Each step is its own durable conversation. Open one to keep talking to it — days
                  later, if you like.
                </p>
              </div>
              {LIVE.has(runStatus) && (
                <>
                  <button onClick={() => void control(runStatus === 'paused' ? 'resume' : 'pause')}>
                    {runStatus === 'paused' ? 'Resume' : 'Pause'}
                  </button>
                  <button className="ghost danger" onClick={() => void control('cancel')}>
                    Cancel
                  </button>
                </>
              )}
            </div>

            {/*
              What the control did, in its own words. Cancelling is the one that
              takes a moment — a step mid-call finishes it first — so the button
              reports what will happen rather than the screen asserting that it
              already has.
            */}
            {effect && (
              <div className="banner warn">
                <p style={{ margin: 0 }}>{effect}</p>
              </div>
            )}

            {/*
              The timeline above the table, because they answer different
              questions and the live one is asked first: the table says what the
              run is made of, and this says what it is doing — including the
              calls that were refused, which is the fact nothing else on this
              screen carries.
            */}
            <RunTimeline runId={activeRunId} />

            {/* What the run changed on disk, which the timeline cannot say. Renders nothing for a run that never wrote a file. */}
            <WorkspaceChangesCard runId={activeRunId} />

            <div className="card">
              <table>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Status</th>
                    <th>Spend</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {steps.map((step) => (
                    <tr key={step.conversationId}>
                      <td>
                        <strong>{step.stepId}</strong>
                        {step.error && (
                          <div style={{ color: 'var(--danger)', fontSize: '0.8rem' }}>{step.error}</div>
                        )}
                      </td>
                      <td>
                        <span className={`pill ${statusTone(step.status)}`}>{step.status}</span>
                      </td>
                      <td>${(step.spendCents / 100).toFixed(2)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="ghost"
                          onClick={() => setStepSelection({ runId: activeRunId, step })}
                        >
                          Open chat
                        </button>
                      </td>
                    </tr>
                  ))}
                  {steps.length === 0 && (
                    <tr>
                      <td colSpan={4} className="hint">
                        This run has not started any steps yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      {selectedStep && (
        <PaneResizer
          label="Step chat width"
          edge="right"
          width={chatWidth}
          min={280}
          max={720}
          reset={400}
          onChange={setChatWidth}
        />
      )}

      {selectedStep && (
        <aside className="inspector">
          <StepChat
            conversationId={selectedStep.conversationId}
            stepName={selectedStep.stepId}
            onFork={() => {
              const message = window.prompt(
                `Fork the run from "${selectedStep.stepId}".\n\nThe new run keeps everything this step had established. What should it do differently?`,
                '',
              );
              if (message !== null) void fork(selectedStep, message);
            }}
          />
          {forking && <p className="hint">Forking…</p>}
        </aside>
      )}
    </div>
  );
}

/**
 * The status filter, in the order a reader scans it: everything, then the two
 * states that might need them, then the two outcomes. The words are the
 * server's (`RunListStatus`) so this row and Home's in-flight panel cannot
 * disagree about which runs are "in flight".
 */
const FILTERS: ReadonlyArray<{ id: RunListStatus; label: string; empty: string }> = [
  { id: 'all', label: 'All', empty: 'Nothing has run' },
  { id: 'in-flight', label: 'In flight', empty: 'Nothing is running' },
  { id: 'waiting', label: 'Waiting', empty: 'Nothing is waiting on a person' },
  { id: 'finished', label: 'Finished', empty: 'Nothing has finished' },
  { id: 'failed', label: 'Failed', empty: 'Nothing has failed' },
];

/**
 * Every recent run, across workflows — what `/runs` opens on.
 *
 * A table rather than the sidebar's list, because the sidebar's rows already
 * know their workflow and these do not: the workflow is the first thing a
 * reader needs from a cross-deployment list, and "started 3m ago on Deploy
 * watch" needs a column for each. Clicking a row hands the pair to the shell,
 * and the detail — with its own sidebar — takes over at the address Slack
 * posts; this component holds nothing about which run is open.
 */
function RunsIndex({ onOpenRun }: { onOpenRun(workflowId: string | null, runId: string | null): void }) {
  const [status, setStatus] = useState<RunListStatus>('all');
  /*
   * Carried with the filter it answers, for the same reason the detail carries
   * its run list with the workflow: a click on Failed while the All response
   * is still in flight must not paint every run under a heading that says
   * only the failed ones are here.
   */
  const [listed, setListed] = useState<{
    status: RunListStatus;
    runs: RunRow[];
    hidden: number;
    windowHours: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which request the screen is showing — the guard `ActionsView` uses, since
  // two filters clicked a moment apart do not have to answer in that order.
  const latest = useRef(0);

  const refresh = useCallback(() => {
    const request = ++latest.current;
    api
      .listRecentRuns({ status })
      .then((result) => {
        if (request !== latest.current) return;
        setListed({ status, runs: result.runs, hidden: result.hidden, windowHours: result.windowHours });
        setError(null);
      })
      .catch((caught) => {
        if (request === latest.current) setError((caught as Error).message);
      });
  }, [status]);

  useEffect(refresh, [refresh]);

  const runs = listed?.status === status ? listed.runs : null;
  const filter = FILTERS.find((entry) => entry.id === status) ?? FILTERS[0]!;
  const windowDays = listed ? Math.round(listed.windowHours / 24) : 7;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Runs</h1>
          <p className="page-lede">
            Every run on the workflows you can see, newest first. Open one for its steps and the
            conversation behind each.
          </p>
        </div>
        <button className="ghost" onClick={refresh}>
          Refresh
        </button>
      </header>

      {error && (
        <div className="banner danger">
          <h3>Something went wrong</h3>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      <div className="filter-row" role="group" aria-label="Status">
        {FILTERS.map((entry) => (
          <button
            key={entry.id}
            className="pill"
            aria-current={entry.id === status}
            onClick={() => setStatus(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <section className="panel">
        {runs === null && !error && <p className="panel-quiet">Loading runs…</p>}

        {runs?.length === 0 && (
          <p className="panel-quiet">
            {filter.empty} in the last {windowDays} days.
          </p>
        )}

        {runs && runs.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="runs-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Workflow</th>
                  <th>Trigger</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Spend</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr
                    key={run.id}
                    className="run-item"
                    data-run-id={run.id}
                    onClick={() => onOpenRun(run.workflowId, run.id)}
                  >
                    <td>
                      {/*
                        The dot carries the tone and the word carries the
                        meaning, as in the sidebar: a column of filled status
                        chips is a column of alerts.
                      */}
                      <span className="run-status">
                        <span className={`run-dot ${statusTone(run.status)}`} aria-hidden="true" />
                        {run.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      {/*
                        The row opens on click; the name is a real button so the
                        same run is reachable from the keyboard, where a `<tr>`
                        with a handler is not.
                      */}
                      <button
                        className="linkish run-workflow"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenRun(run.workflowId, run.id);
                        }}
                      >
                        {run.workflowName}
                      </button>
                    </td>
                    <td className="mono hint">{run.triggerType}</td>
                    <td className="hint">{relativeTime(run.createdAt)}</td>
                    <td className="hint">{duration(run)}</td>
                    <td>${(run.spendCents / 100).toFixed(2)}</td>
                    <td>{run.waiting && <span className="pill warn">waiting</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {listed && listed.status === status && listed.hidden > 0 && (
          <p className="hint" style={{ margin: 0 }}>
            {listed.hidden} more {listed.hidden === 1 ? 'run is' : 'runs are'} on workflows you have
            not been shared on.
          </p>
        )}
      </section>
    </div>
  );
}

/**
 * How long a run took, or has taken so far.
 *
 * `updatedAt` is the last status change, which for a finished run is when it
 * finished. For one still going it is whatever last happened to it, so the
 * clock is read instead — and the row says "so far", because a duration with
 * no qualifier reads as a total.
 */
function duration(run: RunRow): string {
  const started = Date.parse(run.createdAt);
  if (!Number.isFinite(started)) return '—';
  const live = LIVE.has(run.status);
  const ended = live ? Date.now() : Date.parse(run.updatedAt);
  if (!Number.isFinite(ended) || ended < started) return '—';

  const seconds = Math.round((ended - started) / 1000);
  const text =
    seconds < 60
      ? `${seconds}s`
      : seconds < 3600
        ? `${Math.floor(seconds / 60)}m`
        : `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return live ? `${text} so far` : text;
}

/** Statuses a run can still be asked to leave. Mirrors `LIVE_RUN_STATUSES`. */
const LIVE = new Set(['pending', 'running', 'awaiting_approval', 'paused']);

function statusTone(status: string): string {
  if (status === 'completed') return 'ok';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status === 'running') return 'accent';
  return 'warn';
}

/**
 * How long ago, in as few characters as it takes.
 *
 * A run list is scanned for recency, and `8/18/2026, 3:41:23 AM` makes you do
 * the arithmetic yourself on every row. Anything older than a week is a date
 * again, because "9d ago" stops being the useful framing at about that point.
 */
function relativeTime(iso: string): string {
  const then = new Date(iso);
  const seconds = Math.round((Date.now() - then.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
